/**
 * Lenus Cookie Consent
 */

import {
  buildConsentsObject,
  buildCookieConsentConfig,
  getCookiesByCategory,
} from './core/consentConfig'
import {
  initGoogleConsentMode,
  updateGoogleConsentMode,
} from './integrations/googleConsent'
import {
  resolveConfig,
  validateConfig,
  configDefaults,
} from './lib/config/config'

import { createLenusConsentEngine } from '#src/assets/core/consentEngine.ts'
import {
  getPageDeclaredCategories,
  watchPageDeclaredCategories,
} from '#src/assets/integrations/declaredCategories.ts'
import { dispatchConsentEvents } from '#src/assets/integrations/events.ts'
import { processConsentedScripts } from '#src/assets/integrations/scriptProcessing.ts'
import { createApiClient } from '#src/assets/lib/api.ts'
import { createI18nTranslator } from '#src/assets/lib/i18n.ts'
import { createLogger } from '#src/assets/lib/logger.ts'
import { getVisitorId } from '#src/assets/lib/visitor.ts'
import { createCookieConsentEngine } from '#src/assets/ui/mount/cookieConsentEngine.ts'
import { mountCookieConsentUi } from '#src/assets/ui/mount/MountCookieConsentUi.tsx'

const CONFIG = validateConfig(resolveConfig())
const { log } = createLogger(CONFIG.debug)

async function init(): Promise<void> {
  try {
    log('Initializing Lenus Cookie Consent...')
    log('Config:', CONFIG)

    initGoogleConsentMode(log)

    const api = createApiClient({
      config: CONFIG,
      getVisitorId: () => getVisitorId(log),
      log,
    })
    const apiConfig = await api.fetchSiteConfig()
    log('API Config received:', apiConfig)

    const consentEngine = createLenusConsentEngine()

    const t = createI18nTranslator({
      translations: apiConfig.translations,
      locale: apiConfig.locale,
      defaultLocale: configDefaults.defaultLocale,
    })
    const cookiesByCategory = getCookiesByCategory({ apiConfig })
    const engine = createCookieConsentEngine(consentEngine)

    // Scripts are re-processed on every call so gated scripts injected after
    // the last consent change still activate; consent-mode updates and events
    // are only emitted when the consents actually changed, so re-processing
    // does not re-fire them.
    let lastAppliedConsents = ''

    const applyConsents = (consents: Record<string, boolean>): void => {
      const serialized = JSON.stringify(consents)
      const hasChanged = serialized !== lastAppliedConsents
      lastAppliedConsents = serialized

      if (hasChanged) {
        updateGoogleConsentMode(consents, log)
      }

      processConsentedScripts(consents, log)

      if (hasChanged) {
        dispatchConsentEvents({ consents, log })
      }
    }

    const cookieConsentConfig = buildCookieConsentConfig({
      apiConfig,
      cookiesByCategory,
      declaredCategories: getPageDeclaredCategories(),
      locale: apiConfig.locale || 'en-US',
      t,
      onConsentUpdated: (consents, version) => {
        api.postConsentRecord({ consents, version })
        applyConsents(consents)
        const cookieSettingsBtn = document.body.querySelector(
          '#lenus-cookie-settings-btn',
        ) as HTMLElement | null
        if (cookieSettingsBtn) {
          cookieSettingsBtn.style.display = 'block'
        } else {
          log('Cookie settings button not found')
        }
      },
      log,
    })

    const { controller: ui } = mountCookieConsentUi({
      t,
      locale: apiConfig.locale,
      siteConfig: apiConfig.config,
      cookiesByCategory,
      engine,
      log,
    })

    const {
      onFirstConsent: originalOnFirstConsent,
      onChange: originalOnChange,
    } = cookieConsentConfig

    cookieConsentConfig.onFirstConsent = data => {
      originalOnFirstConsent?.(data)
      ui.onConsentSaved()
    }
    cookieConsentConfig.onChange = data => {
      originalOnChange?.(data)
      ui.onConsentSaved()
    }

    log('CookieConsent config:', cookieConsentConfig)
    consentEngine.run(cookieConsentConfig)
    log('Consent engine initialized successfully')

    watchPageDeclaredCategories({
      onDeclaredScriptsAdded: declaredCategories => {
        declaredCategories.forEach(category => {
          cookieConsentConfig.categories[category] ??= {
            enabled: false,
            readOnly: false,
          }
        })
        consentEngine.reloadConsent()

        if (!engine.hasValidConsent()) {
          return
        }

        applyConsents(
          buildConsentsObject({
            acceptedCategories: consentEngine.getCookie().categories,
            allCategories: cookieConsentConfig.categories,
          }),
        )
      },
    })

    if (engine.hasValidConsent()) {
      log('Valid consent found, showing action button')
      ui.showActionButtonIfConsent()

      const existingCookie = consentEngine.getCookie()
      applyConsents(
        buildConsentsObject({
          acceptedCategories: existingCookie.categories,
          allCategories: cookieConsentConfig.categories,
        }),
      )
    } else {
      log('No valid consent found, showing initial banner')
      ui.showBanner()
    }

    window.LenusCookieConsent = {
      showPreferences: () => ui.showPreferencesModal(),
      acceptAll: () => engine.acceptAll(),
      rejectAll: () => engine.rejectAll(),
      getConfig: () => apiConfig,
      getConsent: () => consentEngine.getCookie(),
    }
  } catch (error) {
    console.error('[Lenus Cookie Consent] Initialization failed:', error)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
